import styled, { css, keyframes } from 'styled-components';
// Import Shared Components
import { Alert, Input } from 'shared-components';
// Import Global Components
import Button from '../Button';

// Neutrals from the BP-246 portal mockup; brand and danger colours come from the theme so
// each white label keeps its own.
const M = {
  bg: '#ffffff',
  soft: '#f4f7f5',
  line: '#e1e6e3',
  ink: '#1b2420',
  mute: '#66736d',
  label: '#33403a',
  control: '#8a958f',
  inputLine: '#c7d0cb',
  warnSoft: '#fdf1d6',
  warn: '#8a5a00',
  dangerSoft: '#fbe6e5',
  // The app's on-the-way banner, so both surfaces match.
  successSoft: '#e3f3ea',
  success: '#0b5a2d',
};

const brand = ({ theme }) => theme.brand.primary;
const brandSoft = ({ theme }) => theme.brand.primaryLight || M.soft;
const danger = ({ theme }) => theme.colors.danger;

// Motion only on interactive states (hover, press, selection, focus, enabling): short and
// ease-out; switched off for people who ask for reduced motion.
const EASE = 'ease-out';
const FAST = `150ms ${EASE}`;
const BASE = `200ms ${EASE}`;
const noMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    transition: none;
    transform: none;
  }
`;

const draw = keyframes`
  to { stroke-dashoffset: 0; }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const focusRing = css`
  outline: 2px solid ${brand};
  outline-offset: 2px;
`;

// Dimmed page with the dialog centred on screen, like the portal's other modals. The
// dialog centres itself with margin: auto, so one taller than the screen still scrolls from
// its top instead of being cut off as flex centring would.
export const Scrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  padding: 20px 14px;
  overflow: auto;
  background: rgba(15, 25, 20, 0.5);
`;

// 680px rather than the mockup's 540px (kept by request), and a fixed width so the dialog
// does not resize as the steps change.
export const Dialog = styled.div`
  position: relative;
  margin: auto;
  width: 680px;
  max-width: 100%;
  padding: 22px;
  border-radius: 12px;
  background: ${M.bg};
  color: ${M.ink};
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.3);

  /* Focus lands on the dialog itself when it opens; the controls inside keep their rings. */
  &:focus {
    outline: none;
  }
`;

// One screen of the dialog.
export const StepBody = styled.div`
  display: grid;
  gap: 14px;
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 1;
  width: 32px;
  height: 32px;
  display: grid;
  place-items: center;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: none;
  color: ${M.mute};
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  transition: background-color ${FAST}, color ${FAST};

  &:hover {
    background: ${M.soft};
    color: ${M.ink};
  }

  &:focus-visible {
    ${focusRing}
  }

  ${noMotion}
`;

export const Title = styled.h2`
  margin: 0;
  padding-right: 36px;
  font-size: 19px;
  font-weight: 700;
  color: ${M.ink};
`;

// Reason, Delivery, Confirm: the current and finished steps are filled in.
export const Steps = styled.ol`
  display: flex;
  gap: 6px;
  margin: 8px 0 12px;
  padding: 0;

  li {
    flex: 1;
    list-style: none;
    padding-top: 4px;
    border-top: 3px solid ${M.line};
    font-size: 11.5px;
    font-weight: 600;
    color: ${M.mute};
    transition: border-color ${BASE}, color ${BASE};
    ${noMotion}
  }

  li[data-done='true'] {
    border-top-color: ${brand};
    color: ${brand};
  }
`;

export const FieldLabel = styled.p`
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${M.label};
`;

// The radio or checkbox drawn for an option; the native input stays (hidden) for keyboards
// and screen readers.
export const Indicator = styled.span`
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin-top: 1px;
  display: grid;
  place-items: center;
  border: 2px solid ${M.control};
  border-radius: ${({ $shape }) => ($shape === 'checkbox' ? '5px' : '50%')};
  background: ${M.bg};
  color: ${M.bg};
  transition: border-color ${FAST}, background-color ${FAST}, box-shadow ${FAST};

  /* radio dot */
  &::after {
    content: '';
    display: ${({ $shape }) => ($shape === 'checkbox' ? 'none' : 'block')};
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${brand};
    transform: scale(0);
    transition: transform ${FAST};
  }

  svg {
    width: 12px;
    height: 12px;
    stroke-dasharray: 20;
    stroke-dashoffset: 20;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &::after {
      transition: none;
    }
  }
`;

const visuallyHidden = css`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

// A selectable row (the mockup's .opt): tints on hover, presses in, and fills its control
// when chosen.
export const Option = styled.label`
  position: relative;
  display: flex;
  align-items: ${({ $align }) => $align || 'flex-start'};
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${M.line};
  border-radius: 9px;
  background: ${M.bg};
  color: ${M.ink};
  font-size: 14px;
  cursor: pointer;
  transition: border-color ${FAST}, background-color ${FAST}, transform ${FAST};

  input {
    ${visuallyHidden}
  }

  small {
    display: block;
    color: ${M.mute};
    font-size: 12.5px;
  }

  &:hover {
    border-color: ${M.control};
    background: ${M.soft};
  }

  &:active {
    transform: scale(0.98);
  }

  &:has(input:focus-visible) {
    ${focusRing}
  }

  input:focus-visible + ${Indicator} {
    box-shadow: 0 0 0 3px ${brandSoft};
  }

  ${({ $selected }) =>
    $selected &&
    css`
      &,
      &:hover {
        border-color: ${brand};
        background: ${brandSoft};
      }

      ${Indicator} {
        border-color: ${brand};
      }

      ${Indicator}::after {
        transform: scale(1);
      }

      ${Indicator}[data-shape='checkbox'] {
        background: ${brand};
      }

      ${Indicator} svg {
        animation: ${draw} ${BASE} forwards;
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;

    &:active {
      transform: none;
    }

    ${Indicator} svg {
      animation: none;
      stroke-dashoffset: ${({ $selected }) => ($selected ? 0 : 20)};
    }
  }
`;

// The reason rows, spaced like the rest of the dialog.
export const OptionList = styled.div`
  display: grid;
  gap: 14px;
`;

const BANNER_TONES = {
  warn: css`
    background: ${M.warnSoft};
    color: ${M.warn};
  `,
  info: css`
    background: ${brandSoft};
    color: ${({ theme }) => theme.brand.text || M.ink};
  `,
  bad: css`
    background: ${M.dangerSoft};
    color: ${danger};
  `,
  success: css`
    background: ${M.successSoft};
    color: ${M.success};
  `,
};

// The mockup's banners: a soft fill, no border or icon.
export const Banner = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13.5px;
  line-height: 1.5;
  text-align: left;
  ${({ $tone }) => BANNER_TONES[$tone] || BANNER_TONES.info}
`;

// The dashboard banner keeps the page's spacing between blocks.
export const PageBanner = styled(Banner)`
  margin-bottom: 20px;
`;

// The bold lead-in of a banner, on its own line.
export const BannerLead = styled.b`
  display: block;
`;

export const Address = styled.div`
  padding: 12px;
  border: 1px solid ${M.line};
  border-radius: 9px;
  background: ${M.soft};
  font-size: 14px;
  line-height: 1.5;
`;

// Label and amount rows; the last row is the total. A negative amount reads in red.
export const Summary = styled.dl`
  margin: 0;
  padding: 4px 12px;
  border: 1px solid ${M.line};
  border-radius: 9px;
  font-size: 14px;

  div {
    display: flex;
    justify-content: space-between;
    padding: 7px 0;
    border-bottom: 1px solid ${M.line};
  }

  div:last-child {
    border: 0;
    font-weight: 700;
  }

  dt {
    flex: none;
    margin-right: 16px;
  }

  dd {
    min-width: 0;
    margin: 0;
    text-align: right;
    font-variant-numeric: tabular-nums;
  }

  dd[data-negative='true'] {
    color: ${danger};
  }
`;

// The portal's warning alert, sitting in the dialog's grid (no trailing margin) with its icon
// at the top of the text.
export const DisclosureAlert = styled(Alert)`
  && {
    margin: 0;
    align-items: flex-start;
  }
`;

// The disclosures inside the alert: heading, then the numbered lines.
export const Disclosures = styled.div`
  display: grid;
  gap: 6px;
  font-size: 13px;

  ol {
    display: grid;
    gap: 4px;
    margin: 0;
    padding-left: 18px;
  }

  /* GlobalStyles clears list-style on every li */
  li {
    list-style: decimal;
  }
`;

export const PasswordField = styled.label`
  display: grid;
  gap: 4px;
  font-size: 13px;

  span {
    font-weight: 600;
    color: ${M.label};
  }
`;

export const PasswordInput = styled(Input)`
  && {
    min-height: 0;
    padding: 9px 10px;
    border: 1px solid ${M.inputLine};
    border-radius: 8px;
    box-shadow: none;
    color: ${M.ink};
    font-size: 14px;
    transition: border-color ${FAST}, box-shadow ${FAST};
  }

  &&:focus {
    border-color: ${brand};
    box-shadow: 0 0 0 3px ${brandSoft};
  }

  /* A refused password: the error border the portal's other inputs use, kept on focus. */
  ${({ $invalid }) => $invalid && css`
    &&,
    &&:focus {
      border: ${({ theme }) => theme.borders.error};
    }
  `}

  &&::placeholder {
    color: ${M.mute};
    opacity: 1;
  }

  @media (prefers-reduced-motion: reduce) {
    && {
      transition: none;
    }
  }
`;

export const ErrorText = styled.p`
  margin: 0;
  color: ${danger};
  font-size: 12.5px;
  font-weight: 600;
`;

export const Hint = styled.p`
  margin: 0;
  color: ${M.mute};
  font-size: ${({ $size }) => $size || '12px'};
  text-align: left;
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
`;

// Darkens a filled button on hover and press without knowing its colour.
const shade = css`
  &:hover:not(:disabled) {
    box-shadow: inset 0 0 0 100px rgba(0, 0, 0, 0.08);
  }

  &:active:not(:disabled) {
    box-shadow: inset 0 0 0 100px rgba(0, 0, 0, 0.16);
  }
`;

const BUTTON_TONES = {
  secondary: css`
    background-color: ${M.bg};
    border-color: ${M.line};
    color: ${M.ink};

    &:hover:not(:disabled) {
      background-color: ${M.soft};
      border-color: ${M.control};
    }
  `,
  primary: css`
    background-color: ${brand};
    border-color: ${brand};
    color: ${({ theme }) => theme.brand.primaryText || '#fff'};
    ${shade}
  `,
  danger: css`
    background-color: ${danger};
    border-color: ${danger};
    color: #fff;
    ${shade}
  `,
};

// The mockup's buttons (.mb, .mb.pri, .mb.dng) over the portal Button. Disabled keeps its
// colour at low opacity, and fades in when it becomes available.
export const FlowButton = styled(Button)`
  && {
    display: inline-block;
    width: auto;
    padding: 9px 14px;
    border-width: 1px;
    border-style: solid;
    border-radius: 8px;
    font-size: 13.5px;
    font-weight: 600;
    letter-spacing: normal;
    opacity: 1;
    transition: background-color ${BASE}, border-color ${BASE}, box-shadow ${FAST},
      opacity ${BASE}, transform ${FAST};
    ${({ $tone }) => BUTTON_TONES[$tone] || BUTTON_TONES.secondary}
  }

  &&:active:not(:disabled) {
    top: 0;
    opacity: 1;
    transform: scale(0.98);
  }

  &&:focus-visible {
    ${focusRing}
  }

  &&:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (prefers-reduced-motion: reduce) {
    && {
      transition: none;
    }

    &&:active:not(:disabled) {
      transform: none;
    }
  }
`;

export const Centered = styled.div`
  display: grid;
  gap: 10px;
  text-align: center;

  p {
    margin: 0;
  }
`;

export const Spinner = styled.div`
  width: 34px;
  height: 34px;
  margin: 20px auto;
  border: 4px solid ${M.line};
  border-top-color: ${brand};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;

  /* Still turns, slowly: it is the only sign the order is being placed */
  @media (prefers-reduced-motion: reduce) {
    animation-duration: 3s;
  }
`;

export const SuccessMark = styled.div`
  width: 52px;
  height: 52px;
  margin: 16px auto;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: ${brandSoft};
  color: ${brand};
`;

// "Activate it here" inside the dashboard banner: a button that reads as the banner's links.
export const ActivateLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
`;

// Support phone inside the banners: keeps the banner's text colour, underlined so it reads
// as a link.
export const PhoneLink = styled.a`
  color: inherit;
  font-weight: 600;
  text-decoration: underline;

  &:hover,
  &:focus,
  &:visited {
    color: inherit;
    text-decoration: underline;
  }
`;
